/**
 * Nether structures that are not the fortress: bastion remnants (a treasure
 * room and a bridge-and-hall variant, blackstone with gilded veins, piglin
 * country) and ruined portals. Analytic like netherFortress.ts: every site is
 * a pure function of (seed, region), described as a list of box operations, so
 * each chunk draws its own slice with no neighbour data.
 *
 * Chests: the loot kind of every chest is part of the site plan (`netherLootKind`),
 * and the cell above a chest is always air (nothing targetable steals the ray).
 */

import { CHUNK_SIZE } from '../constants';
import {
  AIR, BEDROCK_ID, BLACKSTONE_ID, CHEST_FACINGS, GILDED_BLACKSTONE_ID, GOLD_BLOCK_ID,
  IRON_BARS_ID, LAVA_ID, MAGMA_ID, NETHERRACK_ID, OBSIDIAN_ID, POLISHED_BLACKSTONE_BRICKS_ID,
} from '../blocks';
import type { LootKind } from '../game/loot';
import { hash2, hash3 } from '../noise';
import { fortressPlan } from './netherFortress';

const AREA = CHUNK_SIZE * CHUNK_SIZE;

export const BASTION_REGION = 400;
export const NETHER_PORTAL_REGION = 224;

export type BastionVariant = 'treasure' | 'bridge';

export interface NetherChest { x: number; y: number; z: number; loot: LootKind }
export interface NetherSite {
  type: 'bastion' | 'ruined_portal';
  variant: BastionVariant | 'portal';
  x: number; y: number; z: number;
  /** Horizontal half-extent of everything the site touches. */
  r: number;
  chests: NetherChest[];
  /** Standing spots for piglins (bastion only). */
  spawns: Array<[number, number, number]>;
  ops: Op[];
  seed: number;
}

interface Op {
  x0: number; y0: number; z0: number; x1: number; y1: number; z1: number;
  /** Candidates, picked per block by hash (repeat an id to weight it). */
  ids: number[];
  /** 'found' fills each column downward from y1 until it meets solid ground. */
  found?: boolean;
}

class Plan {
  readonly ops: Op[] = [];
  readonly chests: NetherChest[] = [];
  readonly spawns: Array<[number, number, number]> = [];
  fill(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, ...ids: number[]): void {
    this.ops.push({
      x0: Math.min(x0, x1), y0: Math.min(y0, y1), z0: Math.min(z0, z1),
      x1: Math.max(x0, x1), y1: Math.max(y0, y1), z1: Math.max(z0, z1), ids,
    });
  }
  set(x: number, y: number, z: number, id: number): void { this.fill(x, y, z, x, y, z, id); }
  /** Foundation under a footprint: fills down from y until solid ground. */
  found(x0: number, z0: number, x1: number, z1: number, y: number, ...ids: number[]): void {
    this.ops.push({ x0: Math.min(x0, x1), y0: y, z0: Math.min(z0, z1), x1: Math.max(x0, x1), y1: y, z1: Math.max(z0, z1), ids, found: true });
  }
  chest(x: number, y: number, z: number, facing: 0 | 1 | 2 | 3, loot: LootKind): void {
    this.set(x, y, z, CHEST_FACINGS[facing]);
    this.chests.push({ x, y, z, loot });
  }
}

const CRYING_OBSIDIAN_ID = 49;
const PB = POLISHED_BLACKSTONE_BRICKS_ID, BS = BLACKSTONE_ID, GB = GILDED_BLACKSTONE_ID;
const WALLMIX = [PB, PB, PB, PB, PB, BS, BS, GB];
const FLOORMIX = [PB, PB, PB, PB, BS, BS, GB];

// --- bastion remnant ---------------------------------------------------------------

/** 25x25 courtyard with a lava moat round a raised dais holding two treasure chests. */
function treasureRoom(p: Plan, cx: number, cz: number, y: number): number {
  const R = 12;
  p.found(cx - R, cz - R, cx + R, cz + R, y - 1, BS, BS, PB);
  p.fill(cx - R, y, cz - R, cx + R, y + 12, cz + R, AIR);
  p.fill(cx - R, y, cz - R, cx + R, y, cz + R, ...FLOORMIX);
  // walls (all four sides), roof, windows of iron bars, doorways in the middle of each side
  p.fill(cx - R, y + 1, cz - R, cx + R, y + 10, cz - R, ...WALLMIX);
  p.fill(cx - R, y + 1, cz + R, cx + R, y + 10, cz + R, ...WALLMIX);
  p.fill(cx - R, y + 1, cz - R, cx - R, y + 10, cz + R, ...WALLMIX);
  p.fill(cx + R, y + 1, cz - R, cx + R, y + 10, cz + R, ...WALLMIX);
  p.fill(cx - R, y + 11, cz - R, cx + R, y + 11, cz + R, BS, PB);
  for (let k = -R + 2; k <= R - 2; k += 5) {
    if (Math.abs(k) <= 2) continue;
    for (const [dx, dz] of [[k, -R], [k, R], [-R, k], [R, k]]) p.fill(cx + dx, y + 5, cz + dz, cx + dx, y + 7, cz + dz, IRON_BARS_ID);
  }
  p.fill(cx - 1, y + 1, cz - R, cx + 1, y + 4, cz - R, AIR);
  p.fill(cx - 1, y + 1, cz + R, cx + 1, y + 4, cz + R, AIR);
  p.fill(cx - R, y + 1, cz - 1, cx - R, y + 4, cz + 1, AIR);
  p.fill(cx + R, y + 1, cz - 1, cx + R, y + 4, cz + 1, AIR);
  // four pillars
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) p.fill(cx + 8 * sx, y + 1, cz + 8 * sz, cx + 8 * sx, y + 10, cz + 8 * sz, PB);
  // lava moat (|d| <= 5) round the 5x5 dais, crossed by 3-wide causeways from the doors
  p.fill(cx - 5, y, cz - 5, cx + 5, y, cz + 5, LAVA_ID);
  p.fill(cx - 5, y - 1, cz - 5, cx + 5, y - 1, cz + 5, BS);
  p.fill(cx - 1, y, cz - 5, cx + 1, y, cz + 5, ...FLOORMIX);
  p.fill(cx - 5, y, cz - 1, cx + 5, y, cz + 1, ...FLOORMIX);
  p.fill(cx - 2, y, cz - 2, cx + 2, y + 1, cz + 2, PB, PB, GB);
  p.set(cx, y + 2, cz, GOLD_BLOCK_ID);
  p.chest(cx - 1, y + 2, cz, 3, 'bastion_treasure');
  p.chest(cx + 1, y + 2, cz, 1, 'bastion_treasure');
  // a store room corner chest on a gilded floor, a pile of gold opposite
  p.set(cx - 9, y, cz - 9, GB);
  p.chest(cx - 9, y + 1, cz - 9, 0, 'bastion_other');
  p.set(cx + 9, y + 1, cz + 9, GOLD_BLOCK_ID);
  for (const [dx, dz] of [[-8, 0], [8, 0], [0, -8], [0, 8], [-9, 9], [9, -9]]) p.spawns.push([cx + dx, y + 1, cz + dz]);
  return R + 1;
}

/** A 13x13 hall with two bridges running out of it, railed, on pillars; one hall chest, one bridge chest. */
function bridgeHall(p: Plan, cx: number, cz: number, y: number): number {
  const H = 6, L = 24;
  p.found(cx - H, cz - H, cx + H, cz + H, y - 1, BS, BS, PB);
  p.fill(cx - H, y, cz - H, cx + H, y + 10, cz + H, AIR);
  p.fill(cx - H, y, cz - H, cx + H, y, cz + H, ...FLOORMIX);
  p.fill(cx - H, y + 1, cz - H, cx + H, y + 7, cz - H, ...WALLMIX);
  p.fill(cx - H, y + 1, cz + H, cx + H, y + 7, cz + H, ...WALLMIX);
  p.fill(cx - H, y + 1, cz - H, cx - H, y + 7, cz + H, ...WALLMIX);
  p.fill(cx + H, y + 1, cz - H, cx + H, y + 7, cz + H, ...WALLMIX);
  p.fill(cx - H, y + 8, cz - H, cx + H, y + 8, cz + H, BS, PB);
  p.fill(cx - H, y + 4, cz - 4, cx - H, y + 5, cz - 4, IRON_BARS_ID);
  p.fill(cx - H, y + 4, cz + 4, cx - H, y + 5, cz + 4, IRON_BARS_ID);
  p.fill(cx - 3, y + 4, cz - H, cx - 3, y + 5, cz - H, IRON_BARS_ID);
  p.fill(cx + 3, y + 4, cz - H, cx + 3, y + 5, cz - H, IRON_BARS_ID);
  p.fill(cx - 3, y + 4, cz + H, cx - 3, y + 5, cz + H, IRON_BARS_ID);
  p.fill(cx + 3, y + 4, cz + H, cx + 3, y + 5, cz + H, IRON_BARS_ID);
  // both bridges: decks, railings, carved headroom, pillars
  for (const s of [-1, 1]) {
    const a = cx + s * (H + 1), b = cx + s * (H + L);
    p.fill(a, y + 1, cz - 3, b, y + 6, cz + 3, AIR);
    p.fill(a, y, cz - 2, b, y, cz + 2, ...FLOORMIX);
    p.fill(a, y - 1, cz - 2, b, y - 1, cz + 2, BS);
    p.fill(a, y + 1, cz - 2, b, y + 1, cz - 2, PB);
    p.fill(a, y + 1, cz + 2, b, y + 1, cz + 2, PB);
    for (let k = 4; k < L; k += 8) p.found(cx + s * (H + k) - 1, cz - 1, cx + s * (H + k) + 1, cz + 1, y - 2, BS, BS, PB);
    p.fill(b, y + 1, cz - 2, b, y + 4, cz - 2, PB); p.fill(b, y + 1, cz + 2, b, y + 4, cz + 2, PB);
    p.fill(b, y + 5, cz - 2, b, y + 5, cz + 2, GB);
    p.spawns.push([cx + s * (H + 8), y + 1, cz]);
  }
  p.chest(cx - H + 1, y + 1, cz + H - 1, 3, 'bastion_other');
  p.chest(cx + H + 8, y + 1, cz - 1, 2, 'bastion_bridge');
  p.set(cx + H + 8, y, cz - 1, GB);
  p.spawns.push([cx - 3, y + 1, cz], [cx + 3, y + 1, cz], [cx, y + 1, cz + 3]);
  return H + L + 1;
}

// --- ruined portal -----------------------------------------------------------------

/** A broken obsidian frame on a netherrack and magma plinth, a gold block, a chest. */
function ruinedPortal(p: Plan, cx: number, cz: number, y: number, seed: number): number {
  const R = 5;
  p.found(cx - R, cz - R, cx + R, cz + R, y - 1, NETHERRACK_ID);
  p.fill(cx - R, y, cz - R, cx + R, y + 8, cz + R, AIR);
  p.fill(cx - R, y, cz - R, cx + R, y, cz + R, NETHERRACK_ID, NETHERRACK_ID, MAGMA_ID);
  p.fill(cx - 3, y, cz - 2, cx + 3, y, cz + 2, BS, BS, PB);
  const alongX = hash2(cx, cz, seed ^ 0x77) < 0.5;
  for (let dy = 1; dy <= 5; dy++) {
    for (let k = -2; k <= 1; k++) {
      const frame = k === -2 || k === 1 || dy === 1 || dy === 5;
      if (!frame) continue;
      if (hash3(cx + k, y + dy, cz, seed ^ 0x61) < (dy >= 4 ? 0.4 : 0.12)) continue;
      const id = hash3(cx + k, y + dy, cz, seed ^ 0x62) < 0.2 ? CRYING_OBSIDIAN_ID : OBSIDIAN_ID;
      if (alongX) p.set(cx + k, y + dy, cz, id); else p.set(cx, y + dy, cz + k, id);
    }
  }
  p.set(cx + 3, y + 1, cz + 2, GOLD_BLOCK_ID);
  p.chest(cx - 4, y + 1, cz - 3, 1, 'ruined_portal');
  return R + 1;
}

// --- sites -------------------------------------------------------------------------------

const cache = new Map<string, NetherSite | null>();

function bastionSite(seed: number, rx: number, rz: number): NetherSite | null {
  const key = 'b' + rx + ',' + rz;
  let s = cache.get(key);
  if (s !== undefined) return s;
  const h = (k: number): number => hash2(rx * 29 + k, rz * 13 - k, seed ^ 0xba57);
  const x = rx * BASTION_REGION + 60 + Math.floor(h(1) * (BASTION_REGION - 120));
  const z = rz * BASTION_REGION + 60 + Math.floor(h(2) * (BASTION_REGION - 120));
  const y = 42 + Math.floor(h(3) * 22);
  const f = fortressPlan(seed, x, z);
  s = null;
  if (h(4) < 0.8 && Math.hypot(x - f.x, z - f.z) > 130) {
    const p = new Plan();
    const variant: BastionVariant = h(5) < 0.5 ? 'treasure' : 'bridge';
    const r = variant === 'treasure' ? treasureRoom(p, x, z, y) : bridgeHall(p, x, z, y);
    s = { type: 'bastion', variant, x, y, z, r, chests: p.chests, spawns: p.spawns, ops: p.ops, seed };
  }
  if (cache.size > 64) cache.clear();
  cache.set(key, s);
  return s;
}

function portalSite(seed: number, rx: number, rz: number): NetherSite | null {
  const key = 'p' + rx + ',' + rz;
  let s = cache.get(key);
  if (s !== undefined) return s;
  const h = (k: number): number => hash2(rx * 17 + k, rz * 31 - k, seed ^ 0x9071);
  const x = rx * NETHER_PORTAL_REGION + 20 + Math.floor(h(1) * (NETHER_PORTAL_REGION - 40));
  const z = rz * NETHER_PORTAL_REGION + 20 + Math.floor(h(2) * (NETHER_PORTAL_REGION - 40));
  const y = 34 + Math.floor(h(3) * 40);
  s = null;
  if (h(4) < 0.7) {
    const p = new Plan();
    const r = ruinedPortal(p, x, z, y, seed);
    s = { type: 'ruined_portal', variant: 'portal', x, y, z, r, chests: p.chests, spawns: [], ops: p.ops, seed };
  }
  if (cache.size > 64) cache.clear();
  cache.set(key, s);
  return s;
}

/** The bastion remnant of the region containing (x, z), or null. */
export function bastionAt(seed: number, x: number, z: number): NetherSite | null {
  return bastionSite(seed, Math.floor(x / BASTION_REGION), Math.floor(z / BASTION_REGION));
}

/** The Nether ruined portal of the region containing (x, z), or null. */
export function netherPortalAt(seed: number, x: number, z: number): NetherSite | null {
  return portalSite(seed, Math.floor(x / NETHER_PORTAL_REGION), Math.floor(z / NETHER_PORTAL_REGION));
}

/** Is (x, y, z) inside a bastion remnant (piglin spawn table)? */
export function inBastion(seed: number, x: number, y: number, z: number): boolean {
  const s = bastionAt(seed, x, z);
  return !!s && Math.abs(x - s.x) <= s.r && Math.abs(z - s.z) <= s.r && y >= s.y - 2 && y <= s.y + 12;
}

/** Loot table of the Nether chest at (x, y, z): bastion / ruined portal chests by plan, else the fortress table. */
export function netherLootKind(seed: number, x: number, y: number, z: number): LootKind {
  for (const s of [bastionAt(seed, x, z), netherPortalAt(seed, x, z)]) {
    const c = s?.chests.find((k) => k.x === x && k.y === y && k.z === z);
    if (c) return c.loot;
  }
  return 'nether_bridge';
}

/** Draw this chunk's slice of every bastion and ruined portal into `out` (after terrain, features, fortress). */
export function placeNetherStructures(seed: number, cx: number, cz: number, out: Uint16Array): void {
  const ox = cx * CHUNK_SIZE, oz = cz * CHUNK_SIZE;
  for (const s of [bastionAt(seed, ox + 8, oz + 8), netherPortalAt(seed, ox + 8, oz + 8)]) {
    if (!s || ox + CHUNK_SIZE <= s.x - s.r - 1 || ox > s.x + s.r + 1 || oz + CHUNK_SIZE <= s.z - s.r - 1 || oz > s.z + s.r + 1) continue;
    for (const op of s.ops) drawOp(s.seed, op, ox, oz, out);
  }
}

function drawOp(seed: number, op: Op, ox: number, oz: number, out: Uint16Array): void {
  const xa = Math.max(op.x0, ox), xb = Math.min(op.x1, ox + CHUNK_SIZE - 1);
  const za = Math.max(op.z0, oz), zb = Math.min(op.z1, oz + CHUNK_SIZE - 1);
  if (xa > xb || za > zb) return;
  const n = op.ids.length;
  for (let z = za; z <= zb; z++) {
    for (let x = xa; x <= xb; x++) {
      const col = x - ox + (z - oz) * CHUNK_SIZE;
      if (op.found) {
        for (let y = op.y1, k = 0; y > 1 && k < 40; y--, k++) {
          const c = out[col + y * AREA];
          if (c !== AIR && c !== LAVA_ID) break;
          out[col + y * AREA] = op.ids[n === 1 ? 0 : Math.floor(hash3(x, y, z, seed) * n)];
        }
        continue;
      }
      for (let y = Math.max(op.y0, 1); y <= Math.min(op.y1, 126); y++) {
        if (out[col + y * AREA] === BEDROCK_ID) continue;
        out[col + y * AREA] = op.ids[n === 1 ? 0 : Math.floor(hash3(x, y, z, seed) * n)];
      }
    }
  }
}
